import { useEffect, type ReactNode } from 'react';
import {
  Clock,
  Mic,
  Shield,
  Users,
  GraduationCap,
  ArrowRight,
  RotateCcw,
  FileText,
  ShieldCheck,
  Archive,
} from 'lucide-react';
import { useStore } from '../store';
import { Button, Eyebrow, PageLoader } from '../components/ui';
import { VTile } from '../components/VMark';
import { Radar, type RadarAxis } from '../components/Radar';
import { BANDS, bandByKey, formatDate, type BandKey } from '../theme/bands';
import { isSampleMode } from '../api/client';
import { currentUser } from '../auth/cognito';
import { sampleReport } from '../sample';
import type { ReportView, SessionSummary } from '../types';

// Per-band hero blurb — the "where you are / what's next" line keyed to the overall band.
const FLUENCY_BLURB: Record<BandKey, string> = {
  early_stage:
    "You're at the start of your AI journey. The fastest gains come from building a few reliable habits you can repeat with confidence.",
  developing:
    "You're using AI for real tasks and building momentum. Your next gains come from prompting more deliberately and knowing where models go wrong.",
  capable:
    "You're confident and consistent with AI across your day-to-day. Your next gains come from designing repeatable workflows and tightening how you review what AI produces.",
  advanced:
    'You bring real judgement to how you work with AI. Your next gains come from templatising what works so your team can run it without you.',
  transformative:
    "You're reshaping how work gets done with AI. Your edge now is scaling that leverage across the team and measuring the lift.",
};

// Roadmap phase dot colours (Developing → Transformative band fills), per the design.
const ROADMAP_DOTS = ['#2438E6', '#B52CF2', '#C2FF01', '#00D700'];

export function Welcome() {
  const setScreen = useStore((s) => s.setScreen);
  const viewSample = useStore((s) => s.viewSample);
  const needsAuth = useStore((s) => s.needsAuth);
  const signIn = useStore((s) => s.signIn);
  const openAssessment = useStore((s) => s.openAssessment);
  const resumable = useStore((s) => s.resumable);
  const resumeAssessment = useStore((s) => s.resumeAssessment);
  const archiveAssessment = useStore((s) => s.archiveAssessment);
  const loadWelcome = useStore((s) => s.loadWelcome);
  const liveReport = useStore((s) => s.report);
  const welcomeLoading = useStore((s) => s.welcomeLoading);

  // The dashboard renders when there's a completed report; sample mode uses the fixture.
  const report = liveReport ?? (isSampleMode ? sampleReport : null);

  useEffect(() => loadWelcome(), [loadWelcome]);

  // While the report fetch is in flight on boot, hold on the loading veil rather than
  // rendering the get-started hero and flipping to the Dashboard when it arrives.
  if (welcomeLoading && !report) return <PageLoader />;

  const start = () => {
    if (needsAuth) {
      signIn(); // redirect to the Cognito Hosted UI; returns to the app authenticated
      return;
    }
    openAssessment(); // resumes the active assessment if one exists, else starts fresh
  };

  return (
    <main className="mx-auto max-w-[1180px] px-8 pb-[72px] pt-10">
      {resumable && (
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3 rounded-card border border-panel bg-faint p-4">
          <div className="flex items-center gap-3">
            <span className="text-green-safe">
              <RotateCcw size={20} />
            </span>
            <div>
              <div className="text-[15px] font-bold text-code">You have an assessment in progress</div>
              <div className="text-[13px] text-storm">
                Stage {resumable.stage} of 3 · pick up right where you left off.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => resumeAssessment(resumable.assessmentId)}>
              Continue <ArrowRight size={16} />
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                if (
                  window.confirm(
                    'Archive your in-progress assessment and start a new one? Your current progress will be set aside.',
                  )
                ) {
                  archiveAssessment(true);
                }
              }}
            >
              Archive & start fresh
            </Button>
          </div>
        </div>
      )}

      {report ? (
        <Dashboard
          report={report}
          onReport={() => setScreen('report')}
          onPractice={() => setScreen('practice')}
        />
      ) : (
        <GetStarted needsAuth={needsAuth} onStart={start} onSample={viewSample} />
      )}
    </main>
  );
}

// ---- Completed-state dashboard (v3 design) -----------------------------------------

// The active campaign label shown in the dashboard context line.
const CAMPAIGN_LABEL = 'Q2 AI Fluency Uplift';

// roleLine is like "Senior Presales Consultant · Go-to-Market · Assessed 12 Jun 2026".
// Keep the role + team segments; drop the trailing "Assessed …" segment.
function roleAndTeam(roleLine?: string): string {
  if (!roleLine) return '';
  return roleLine
    .split('·')
    .map((s) => s.trim())
    .filter((s) => s && !/^assessed\b/i.test(s))
    .join(' · ');
}

function Dashboard({
  report,
  onReport,
  onPractice,
}: {
  report: ReportView;
  onReport: () => void;
  onPractice: () => void;
}) {
  const history = useStore((s) => s.history);
  const loadHistory = useStore((s) => s.loadHistory);
  const openReport = useStore((s) => s.openReport);
  const resumeAssessment = useStore((s) => s.resumeAssessment);
  const archiveAssessment = useStore((s) => s.archiveAssessment);

  useEffect(() => loadHistory(), [loadHistory]);

  const overall = bandByKey(report.overallReadiness.band);
  const axes: RadarAxis[] = report.criteria.map((c) => ({
    criterion: c.criterion,
    band: c.band.band,
    level: bandByKey(c.band.band).level,
  }));

  // The live report payload is band-only (no name); fall back to the signed-in identity so
  // the header shows the real user, not a blank or the demo persona.
  const identity = report.name ?? currentUser()?.name ?? currentUser()?.email;
  const context = [identity, roleAndTeam(report.roleLine)].filter(Boolean).join(' · ');
  const total = history?.length ?? 0;
  const complete = history?.filter((a) => a.status === 'completed').length ?? 0;

  return (
    <div className="animate-fadeUp bg-white">
      {/* header */}
      <div className="mb-[30px] flex flex-wrap items-end justify-between gap-6 border-b border-divider pb-[22px]">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-[1.05] tracking-[-0.02em] text-black">Dashboard</h1>
          <div className="mt-[9px] text-[14px] text-storm">
            {context}
            {context && ' · '}
            <span className="font-semibold text-rock">{CAMPAIGN_LABEL}</span>
          </div>
        </div>
        <span className="inline-flex items-center gap-[9px] rounded-full border border-panel bg-faint px-[15px] py-[9px]">
          <span className="h-2 w-2 rounded-full bg-green" aria-hidden="true" />
          <span className="text-[13px] font-semibold text-rock">Assessment complete</span>
        </span>
      </div>

      {/* result strip */}
      <div className="flex flex-wrap items-center gap-[44px] rounded-cardlg border border-panel px-[34px] py-[30px]">
        <div className="min-w-[300px] flex-1">
          <Eyebrow>Current result</Eyebrow>
          <div className="mb-[13px] mt-[14px] flex flex-wrap items-end gap-[13px]">
            <span
              className="mb-2 h-[14px] w-[14px] flex-none rounded-[4px]"
              style={{ background: overall.fill }}
              aria-hidden="true"
            />
            <span className="text-[40px] font-extrabold leading-none tracking-[-0.03em] text-black tabular-nums">
              {overall.label}
            </span>
            <span className="mb-[6px] text-[13px] font-semibold text-storm">band {overall.level} of 5</span>
          </div>
          <p className="mb-[22px] max-w-[52ch] text-[15px] leading-[1.55] text-rock">
            {FLUENCY_BLURB[report.overallReadiness.band]}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={onReport}>
              View full report <ArrowRight size={16} />
            </Button>
            <Button variant="ghost" onClick={onPractice}>
              Continue practice
            </Button>
          </div>
        </div>
        <div className="flex flex-none flex-col items-center gap-[9px]">
          <div className="h-[176px] w-[176px]">
            <Radar axes={axes} compact />
          </div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-storm">Across five criteria</div>
        </div>
      </div>

      {/* my assessments */}
      <div className="mb-[14px] mt-[42px] flex items-baseline justify-between">
        <Eyebrow>My assessments</Eyebrow>
        <div className="text-[13px] text-storm tabular-nums">
          {total} total · {complete} complete
        </div>
      </div>
      {history && (
        <div className="overflow-x-auto">
          <div className="min-w-[560px] overflow-hidden rounded-card border border-panel">
            <div
              className="grid items-center gap-[18px] border-b border-panel bg-faint px-6 py-[13px]"
              style={{ gridTemplateColumns: '1.5fr 0.8fr 1.1fr auto' }}
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-storm">Status</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-storm">Stage</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-storm">Started</span>
              <span />
            </div>
            {history.map((a) => (
              <AssessmentRow
                key={a.assessmentId}
                summary={a}
                onReport={() => openReport(a.assessmentId)}
                onResume={() => resumeAssessment(a.assessmentId)}
                onArchive={() => {
                  if (
                    window.confirm(
                      'Archive this in-progress assessment and start a new one? Your current progress will be set aside.',
                    )
                  ) {
                    archiveAssessment(true, a.assessmentId);
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 90-day plan */}
      <div className="mb-4 mt-10 flex items-baseline justify-between">
        <Eyebrow>Your 90-day plan</Eyebrow>
        <button
          onClick={onReport}
          className="inline-flex items-center gap-[6px] text-[13px] font-semibold text-rock transition-colors hover:text-green-safe"
        >
          View full roadmap <ArrowRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {report.roadmap.map((phase, i) => (
          <RoadmapCard key={phase.window} phase={phase} current={i === 1} dot={ROADMAP_DOTS[i] ?? '#75757F'} />
        ))}
      </div>

      {/* privacy */}
      <div className="mt-[34px] flex items-center gap-3 rounded-[10px] border border-panel bg-faint px-[18px] py-[15px]">
        <span className="flex-none text-black">
          <ShieldCheck size={18} strokeWidth={1.7} />
        </span>
        <span className="text-[13.5px] leading-[1.5] text-rock">
          <strong className="font-bold text-black">Private by design.</strong> Your responses build your report, for
          you. Leaders only ever see anonymised, role-level trends — never your raw answers.
        </span>
      </div>
    </div>
  );
}

function AssessmentRow({
  summary,
  onReport,
  onResume,
  onArchive,
}: {
  summary: SessionSummary;
  onReport: () => void;
  onResume: () => void;
  onArchive: () => void;
}) {
  const completed = summary.status === 'completed';
  const dot = completed ? '#00D700' : '#E8A400';
  const label = completed ? 'Complete' : 'In progress';

  return (
    <div
      className="grid items-center gap-[18px] border-t border-divider px-6 py-[17px] transition-colors hover:bg-[#FAFAFB]"
      style={{ gridTemplateColumns: '1.5fr 0.8fr 1.1fr auto' }}
    >
      <span className="inline-flex items-center gap-[11px]">
        <span className="h-[9px] w-[9px] flex-none rounded-full" style={{ background: dot }} aria-hidden="true" />
        <span className="text-[15px] font-bold tracking-[-0.01em] text-black">{label}</span>
      </span>
      <span className="text-[14px] text-rock tabular-nums">{summary.stage} of 3</span>
      <span className="text-[14px] text-storm tabular-nums">{formatDate(summary.startedAt)}</span>
      <span className="justify-self-end">
        {completed ? (
          <button
            onClick={onReport}
            className="inline-flex items-center gap-2 rounded-ctl border-[1.5px] border-black bg-white px-4 py-2 text-[13.5px] font-semibold text-black transition-colors hover:bg-black hover:text-white"
          >
            <FileText size={15} strokeWidth={1.9} /> View report
          </button>
        ) : (
          <span className="inline-flex items-center gap-2">
            <button
              onClick={onArchive}
              className="inline-flex items-center gap-2 rounded-ctl border-[1.5px] border-panel bg-white px-[14px] py-2 text-[13.5px] font-semibold text-storm transition-colors hover:border-rock hover:text-rock"
              title="Archive this assessment and start a new one"
            >
              <Archive size={15} strokeWidth={1.9} /> Archive & start fresh
            </button>
            <button
              onClick={onResume}
              className="inline-flex items-center gap-2 rounded-ctl border-2 border-green bg-green px-[18px] py-[9px] text-[13.5px] font-bold text-black transition-colors hover:border-green-hover hover:bg-green-hover"
            >
              <RotateCcw size={15} strokeWidth={2.2} /> Continue
            </button>
          </span>
        )}
      </span>
    </div>
  );
}

function RoadmapCard({
  phase,
  current,
  dot,
}: {
  phase: { window: string; title: string; body: string };
  current: boolean;
  dot: string;
}) {
  return (
    <div
      className={`rounded-card border p-4 ${current ? 'border-black bg-white' : 'border-panel bg-faint'}`}
    >
      <div className="mb-[11px] flex min-h-[20px] items-center justify-between">
        <span className="h-[10px] w-[10px] rounded-[3px]" style={{ background: dot }} aria-hidden="true" />
        {current && (
          <span
            className="rounded-[4px] px-[7px] py-[3px] text-[10px] font-bold uppercase tracking-[0.1em] text-black"
            style={{ background: '#C2FF01' }}
          >
            Now
          </span>
        )}
      </div>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-storm">{phase.window}</div>
      <div className="mt-1 text-[14.5px] font-bold tracking-[-0.01em] text-black">{phase.title}</div>
    </div>
  );
}

// ---- First-time / empty state (no completed assessment yet) ------------------------

function GetStarted({
  needsAuth,
  onStart,
  onSample,
}: {
  needsAuth: boolean;
  onStart: () => void;
  onSample: () => void;
}) {
  return (
    <>
      <div className="grid items-start gap-16 lg:grid-cols-[1.05fr_0.95fr]">
        {/* hero left */}
        <div className="animate-fadeUp">
          <span className="inline-flex items-center gap-2 rounded-full border border-panel px-3 py-1 text-[11px] font-bold tracking-[0.16em] text-storm">
            <span className="h-2 w-2 rounded-full bg-green" /> AI FLUENCY ASSESSMENT
          </span>
          <h1 className="mt-5 text-[60px] font-bold leading-[1.02] tracking-display text-code">
            Find your AI fluency.
            <br />
            <span className="text-green-safe">In your own words.</span>
          </h1>
          <p className="mt-5 max-w-[34rem] text-[19px] leading-[1.55] text-rock">
            Avery interviews you for about half an hour, then gives you a clear, coaching-oriented picture of where you
            are and what to do next. No quizzes, no scores — just a real conversation about how you work.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Chip icon={<Clock size={15} />}>25–35 min</Chip>
            <Chip icon={<Mic size={15} />}>Type or speak — voice-to-text welcome</Chip>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={onStart} glow>
              {needsAuth ? 'Sign in to start' : 'Start assessment'} <ArrowRight size={17} />
            </Button>
            <Button variant="ghost" onClick={onSample}>
              See a sample report
            </Button>
          </div>
        </div>

        {/* meet Avery card */}
        <div className="animate-fadeUp rounded-card bg-white shadow-float" style={{ animationDelay: '80ms' }}>
          <div className="flex items-center gap-3 rounded-t-card bg-code p-5 text-white">
            <span className="relative inline-flex">
              <VTile size={46} />
              <span className="pointer-events-none absolute inset-0 animate-pulseRing rounded-ctl ring-2 ring-green" />
            </span>
            <div className="flex-1">
              <div className="text-[16px] font-bold">Meet Avery</div>
              <div className="text-[12.5px] text-storm">Your AI-fluency assessor</div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold"
              style={{ background: '#121218', color: '#00D700' }}
            >
              <span className="h-2 w-2 rounded-full bg-green" /> Ready
            </span>
          </div>
          <div className="p-5">
            <div className="relative max-w-[85%] rounded-card rounded-bl-sm bg-subtle p-3.5 text-[14px] text-rock">
              No wrong answers here — I just want to understand how you actually use AI day to day.
            </div>
            <Eyebrow>
              <span className="mt-5 block">Three stages</span>
            </Eyebrow>
            <ol className="mt-3 space-y-2.5">
              <Stage n={1} title="Workflow walkthrough" time="~12 min" />
              <Stage n={2} title="Leverage snapshot" time="~10 min" />
              <Stage n={3} title="Coaching & roadmap" time="~8 min" />
            </ol>
          </div>
        </div>
      </div>

      {/* reassurance row */}
      <div className="mt-16 grid gap-4 md:grid-cols-3">
        <Reassure
          icon={<Shield size={20} />}
          title="Private by design"
          body="Your responses build your report and are stored in-region. You can delete them at any time."
        />
        <Reassure
          icon={<Users size={20} />}
          title="Just you and Avery"
          body="Please complete the assessment yourself, without another AI helping. It keeps your result honest and useful."
        />
        <Reassure
          icon={<GraduationCap size={20} />}
          title="Coaching, not grading"
          body="You'll see capability bands and next steps — never a pass/fail or a raw score."
        />
      </div>

      {/* band legend */}
      <div className="mt-12 rounded-card border border-panel bg-subtle p-5">
        <Eyebrow>The capability scale</Eyebrow>
        <div className="mt-3 grid gap-2 sm:grid-cols-5">
          {BANDS.map((b) => (
            <div key={b.key} className="rounded-ctl p-3" style={{ background: b.fill, color: b.text }}>
              <div className="text-[12.5px] font-bold">{b.label}</div>
              <div className="mt-1 text-[11px] leading-snug opacity-90">{b.descriptor}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Chip({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-ctl border border-panel bg-white px-3 py-1.5 text-[13px] text-rock">
      <span className="text-storm">{icon}</span>
      {children}
    </span>
  );
}

function Stage({ n, title, time }: { n: number; title: string; time: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-code text-[12px] font-bold text-white">
        {n}
      </span>
      <span className="flex-1 text-[14px] font-semibold text-rock">{title}</span>
      <span className="text-[12px] text-storm">{time}</span>
    </li>
  );
}

function Reassure({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-card border border-panel bg-white p-5">
      <span className="text-green-safe">{icon}</span>
      <div className="mt-3 text-[15.5px] font-bold text-code">{title}</div>
      <p className="mt-1.5 text-[13.5px] leading-[1.5] text-storm">{body}</p>
    </div>
  );
}
